/**
 * Stale-bundle detector + force-reload (Jun 23, 2026; recalibrated Mar 2026).
 *
 * Two production incidents on Jun 23 (classroom 318 + 639) were both
 * caused by mobile browsers serving the user a stale JS bundle from
 * disk cache after we shipped a fix. We can't retroactively clear
 * users' caches, but we CAN tell each client "you're stale, please
 * reload" by polling a backend version endpoint.
 *
 * Architecture
 * ────────────
 *   1. On first /api/version response we adopt the server-reported
 *      `server_version` AND `frontend_hash` as our baseline.
 *      Important: we do NOT seed the baseline from the loaded
 *      <script src> hash — Emergent's production topology can have
 *      the frontend CDN and the backend container's mounted
 *      `asset-manifest.json` drift slightly out of sync, which would
 *      falsely flag every tab as stale forever (Mar 2 incident).
 *   2. Every 60 s — and on visibilitychange→visible, which catches the
 *      "user re-opened a tab from yesterday" case — we re-fetch
 *      /api/version with cache-bypass.
 *   3. If either `server_version` OR `frontend_hash` ever differs
 *      from the baseline we adopted, we flip `updateAvailable` true.
 *      That is the SAME server reporting a different value over time,
 *      i.e. a real new deploy.
 *   4. <UpdateRequiredModal> renders a non-dismissable overlay; the
 *      user can only proceed by reloading.
 *   5. The reload handler best-effort-clears Cache Storage and
 *      unregisters the service worker so the next fetch hits origin.
 *      Auth state in localStorage is intentionally preserved.
 *
 * Implementation note: we deliberately use `useSyncExternalStore` and
 * a hand-rolled mini-store instead of pulling in a state library —
 * Zustand isn't in package.json and we don't want to bloat the bundle
 * for one screen of state.
 */
import { useSyncExternalStore } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const POLL_INTERVAL_MS = 5 * 60 * 1000; // Nov 2026 (Support fix #4): 60s → 5min
const FIRST_CHECK_DELAY_MS = 5 * 1000;
const RELOAD_LOOP_GUARD_KEY = 'mesp_last_force_reload_at';
const RELOAD_LOOP_WINDOW_MS = 30 * 1000;

// ─── Tiny store with useSyncExternalStore subscribers ─────────────
// NOTE — `initialFrontendHash` starts null and is filled exclusively
// by the first successful /api/version response. We used to seed it
// from the live <script src> in the DOM, but that breaks when the
// CDN-served bundle hash and the backend container's mounted
// `asset-manifest.json` are out of sync (Mar 2026: prod was serving
// `main.3adac66c.js` while /api/version reported `c74ea171`, causing
// the modal to fire on every poll forever).
const _state = {
  initialServerVersion: null,
  initialFrontendHash: null,
  latestServerVersion: null,
  latestFrontendHash: null,
  updateAvailable: false,
  isReloading: false,
  lastCheckAt: null,
  lastCheckOk: null,
  // Bumped on every setState so getSnapshot returns a NEW reference
  // and useSyncExternalStore actually triggers a re-render. Without
  // this, React's Object.is check on the same `_state` object would
  // silently skip the update — defeating the entire modal.
  _rev: 0,
};
const _listeners = new Set();
const _emit = () => { for (const l of _listeners) l(); };

const setState = (partial) => {
  Object.assign(_state, partial);
  _state._rev += 1;
  _snapshot = { ..._state };
  _emit();
};

const subscribe = (cb) => {
  _listeners.add(cb);
  return () => _listeners.delete(cb);
};

// Cached immutable snapshot — a NEW reference on every setState so
// useSyncExternalStore correctly re-renders subscribers.
let _snapshot = { ..._state };
const getSnapshot = () => _snapshot;

export const useVersionState = () => useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

// ─── Logic for processing a /api/version response ─────────────────
const applyVersion = (data) => {
  if (_state.updateAvailable) return; // already flagged — stop processing
  const serverV = data?.server_version || null;
  const frontH = data?.frontend_hash || null;
  // First successful response → adopt as the baseline.
  // We trust ONLY the server-reported values. If the server later
  // reports a different value for the same key, that means a real
  // new deploy happened and we should prompt the user to reload.
  if (_state.initialServerVersion === null && serverV) {
    setState({
      initialServerVersion: serverV,
      latestServerVersion: serverV,
      initialFrontendHash: frontH,
      latestFrontendHash: frontH,
      lastCheckAt: Date.now(),
      lastCheckOk: true,
    });
    return;
  }
  setState({
    latestServerVersion: serverV,
    latestFrontendHash: frontH,
    lastCheckAt: Date.now(),
    lastCheckOk: true,
  });
  const stale =
    (serverV && _state.initialServerVersion && serverV !== _state.initialServerVersion)
    || (frontH && _state.initialFrontendHash && frontH !== _state.initialFrontendHash);
  if (stale) {
    setState({ updateAvailable: true });
    console.warn('[version-check] stale bundle detected', {
      initial_server: _state.initialServerVersion,
      latest_server: serverV,
      initial_front: _state.initialFrontendHash,
      latest_front: frontH,
    });
  }
};

// ─── Fetcher ──────────────────────────────────────────────────────
const fetchVersion = async () => {
  if (!API_URL) return null;
  try {
    // Nov 2026 — no `?_=` cache-buster: the endpoint already sends
    // no-store headers; the query string only defeated CDN/edge caching.
    const url = `${API_URL}/api/version`;
    const resp = await fetch(url, {
      method: 'GET',
      credentials: 'omit', // public endpoint, no auth needed
      cache: 'no-store',
    });
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    return await resp.json();
  } catch (e) {
    console.warn('[version-check] fetch failed:', e?.message || e);
    setState({ lastCheckAt: Date.now(), lastCheckOk: false });
    return null;
  }
};

let _pollHandle = null;
let _initialized = false;

const tickOnce = async () => {
  if (_state.updateAvailable) return;
  const data = await fetchVersion();
  if (data) applyVersion(data);
};

/**
 * Start the polling loop. Idempotent — safe to call multiple times.
 * Returns a cleanup function suitable for `useEffect`.
 */
export const startVersionPolling = () => {
  if (_initialized) return () => {};
  _initialized = true;

  const initialTimeout = setTimeout(tickOnce, FIRST_CHECK_DELAY_MS);
  _pollHandle = setInterval(tickOnce, POLL_INTERVAL_MS);

  const onVisible = () => {
    if (document.visibilityState === 'visible') tickOnce();
  };
  document.addEventListener('visibilitychange', onVisible);

  return () => {
    clearTimeout(initialTimeout);
    if (_pollHandle) clearInterval(_pollHandle);
    document.removeEventListener('visibilitychange', onVisible);
    _initialized = false;
  };
};

/**
 * Force a hard reload. Best-effort clears Cache Storage + service
 * worker registration before reloading so the next fetch hits origin.
 * Auth state in localStorage is preserved.
 */
export const forceHardReload = async () => {
  setState({ isReloading: true });

  // Loop guard: if we reloaded <30 s ago and we're STILL stale, the
  // deploy is broken. Don't grind the user's browser.
  try {
    const last = parseInt(sessionStorage.getItem(RELOAD_LOOP_GUARD_KEY) || '0', 10);
    if (last && Date.now() - last < RELOAD_LOOP_WINDOW_MS) {
      console.error('[version-check] reload-loop detected, aborting');
      setState({ isReloading: false });
      return;
    }
    sessionStorage.setItem(RELOAD_LOOP_GUARD_KEY, String(Date.now()));
  } catch (_e) { /* sessionStorage may be unavailable */ }

  try {
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch (_e) { /* ignore */ }
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
  } catch (_e) { /* ignore */ }

  try {
    const u = new URL(window.location.href);
    u.searchParams.set('_v', String(Date.now()));
    window.location.replace(u.toString());
  } catch (_e) {
    window.location.reload();
  }
};
